<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { storeToRefs } from 'pinia'

  import { useAppRouting } from '@/composables/useAppRouting'
  import { useTaskFrontpage } from '@/composables/useTaskFrontpage'
  import { Routes } from '@/const/Routes'
  import { useAuthStore } from '@/stores/auth'

  import ImageWithCaption from '@/components/common/ImageWithCaption.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import Typography from '@/components/common/Typography.vue'
  import VgsButton from '@/components/common/VgsButton.vue'
  import VgsSpinner from '@/components/common/VgsSpinner.vue'

  const props = withDefaults(defineProps<{
    contentId: number
    isFallbackTextImage?: boolean // Can be deleted after VGS-473
  }>(), {
    isFallbackTextImage: false
  })

  const { isTeacher } = storeToRefs(useAuthStore())
  const { t } = useI18n()
  const { getRouteWithParams } = useAppRouting()
  const {
    src,
    altText,
    caption,
    copyright,
    taskFrontpage,
    parentElement,
    resourceGuide,
  } = useTaskFrontpage(props.contentId, props.isFallbackTextImage) // Second argument can be deleted after VGS-473
</script>

<template>
  <main
    v-if="taskFrontpage && src"
    :class="['task-frontpage', taskFrontpage.colorPair]"
  >
    <ImageWithCaption
      v-if="src"
      class="image"
      :src="src"
      :alt-text="altText"
      :caption="caption"
      :copyright="copyright"
      :object-fit="'cover'"
    />
    <div class="text-wrapper">
      <Typography
        v-if="parentElement?.title"
        variant="h2"
        component="h1"
      >
        {{ parentElement.title }}
      </Typography>
      <RichTextRenderer :rich-text="taskFrontpage.description || parentElement?.description" />
      <router-link
        v-if="isTeacher && resourceGuide"
        :to="getRouteWithParams(Routes.ResourceGuide, resourceGuide)"
      >
        <VgsButton
          variant="flat-sharp"
          data-track="enkeltoppgave-lærerveiledning-knapp"
          icon-left="info-circle"
        >
          {{ t('subject.to-teacher') }}
        </VgsButton>
      </router-link>
    </div>
  </main>
  <VgsSpinner v-else is-loading />
  <slot name="footer" />
</template>

<style scoped lang="scss">
  @include color-pairs;

  .task-frontpage {
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
  }

  .text-wrapper {
    --text-wrapper-margin: 128px;

    position: absolute;
    display: flex;
    flex-direction: column;
    padding: 4.5vw;
    margin-left: var(--text-wrapper-margin);
    background: var(--color-pair-background-color);
    color: var(--color-pair-color);

    @include vgs-media-desktop {
      width: calc(50% - var(--text-wrapper-margin));
    }

    @include vgs-media-mobile-and-tablet {
      margin: 0 24px;
      justify-content: center;
      padding: 64px 24px;
    }

    @include vgs-media-mobile {
      bottom: 128px;
    }

    button {
      margin-top: 24px;
    }

    p {
      margin-top: 8px;
    }
  }

  .image {
    height: 100%;
  }

  :deep(img) {
    @include vgs-media-mobile {
      object-position: 80% center;
    }
  }
</style>
